import React, { useState } from 'react';
import { 
  User, 
  Mail, 
  Phone, 
  Shield, 
  Edit, 
  Save, 
  X,
  Lock,
  Calendar,
  MapPin
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const Profil = () => {
  const { currentUser, hasRole } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    nom: currentUser?.nom || '',
    email: currentUser?.email || '',
    telephone: '+33 6 12 34 56 78',
    adresse: '4 Quai d\'Arenc, 13002 Marseille',
    poste: 'Responsable des opérations'
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSave = () => {
    // Ici on sauvegarderait les modifications
    console.log('Sauvegarde du profil:', formData);
    setIsEditing(false);
  };

  const getRoleInfo = (role) => {
    const roles = {
      admin: { label: 'Administrateur', color: 'bg-red-100 text-red-800', description: 'Accès complet à toutes les fonctionnalités' },
      manager: { label: 'Manager', color: 'bg-blue-100 text-blue-800', description: 'Gestion des équipes et des opérations' },
      operateur: { label: 'Opérateur', color: 'bg-green-100 text-green-800', description: 'Exécution des tâches quotidiennes' }
    };
    return roles[role] || { label: role, color: 'bg-gray-100 text-gray-800', description: 'Rôle non défini' };
  };

  const roleInfo = getRoleInfo(currentUser?.role);

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-port-gray-900">Mon profil</h1>
          <p className="text-port-gray-600">Gérer vos informations personnelles</p>
        </div>
        <div className="flex items-center space-x-3">
          {isEditing ? (
            <>
              <button 
                onClick={() => setIsEditing(false)}
                className="btn-secondary flex items-center space-x-2"
              >
                <X className="h-5 w-5" />
                <span>Annuler</span>
              </button>
              <button 
                onClick={handleSave}
                className="btn-primary flex items-center space-x-2"
              >
                <Save className="h-5 w-5" />
                <span>Enregistrer</span>
              </button>
            </>
          ) : (
            <button 
              onClick={() => setIsEditing(true)}
              className="btn-primary flex items-center space-x-2"
            >
              <Edit className="h-5 w-5" />
              <span>Modifier</span>
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Informations principales */}
        <div className="lg:col-span-2 space-y-6">
          {/* Informations personnelles */}
          <div className="card p-6">
            <h3 className="text-lg font-semibold text-port-gray-900 mb-4 flex items-center">
              <User className="h-5 w-5 mr-2" />
              Informations personnelles
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-port-gray-700 mb-2">
                  Nom complet
                </label>
                {isEditing ? (
                  <input
                    type="text"
                    name="nom"
                    value={formData.nom}
                    onChange={handleChange}
                    className="input-field"
                  />
                ) : (
                  <p className="text-port-gray-900">{formData.nom}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-port-gray-700 mb-2">
                  Poste
                </label>
                {isEditing ? (
                  <input
                    type="text"
                    name="poste"
                    value={formData.poste}
                    onChange={handleChange}
                    className="input-field"
                  />
                ) : (
                  <p className="text-port-gray-900">{formData.poste}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-port-gray-700 mb-2">
                  Email
                </label>
                {isEditing ? (
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="input-field"
                  />
                ) : (
                  <div className="flex items-center">
                    <Mail className="h-4 w-4 text-port-gray-400 mr-2" />
                    <p className="text-port-gray-900">{formData.email}</p>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-port-gray-700 mb-2">
                  Téléphone
                </label>
                {isEditing ? (
                  <input
                    type="tel"
                    name="telephone"
                    value={formData.telephone}
                    onChange={handleChange}
                    className="input-field"
                  />
                ) : (
                  <div className="flex items-center">
                    <Phone className="h-4 w-4 text-port-gray-400 mr-2" />
                    <p className="text-port-gray-900">{formData.telephone}</p>
                  </div>
                )}
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-port-gray-700 mb-2">
                  Adresse
                </label>
                {isEditing ? (
                  <input
                    type="text"
                    name="adresse"
                    value={formData.adresse}
                    onChange={handleChange}
                    className="input-field"
                  />
                ) : (
                  <div className="flex items-center">
                    <MapPin className="h-4 w-4 text-port-gray-400 mr-2" />
                    <p className="text-port-gray-900">{formData.adresse}</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Informations de sécurité */}
          <div className="card p-6">
            <h3 className="text-lg font-semibold text-port-gray-900 mb-4 flex items-center">
              <Lock className="h-5 w-5 mr-2" />
              Sécurité
            </h3>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-port-gray-50 rounded-lg">
                <div>
                  <h4 className="font-medium text-port-gray-900">Mot de passe</h4>
                  <p className="text-sm text-port-gray-600">Dernière modification il y a 30 jours</p>
                </div>
                <button className="btn-secondary">Modifier</button>
              </div>
              
              <div className="flex items-center justify-between p-4 bg-port-gray-50 rounded-lg">
                <div>
                  <h4 className="font-medium text-port-gray-900">Authentification à deux facteurs</h4>
                  <p className="text-sm text-port-gray-600">Non activée</p>
                </div>
                <button className="btn-secondary">Activer</button>
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar avec informations du compte */}
        <div className="space-y-6">
          {/* Avatar et rôle */}
          <div className="card p-6">
            <div className="text-center">
              <div className="h-20 w-20 rounded-full bg-atl-blue-100 flex items-center justify-center mx-auto mb-4">
                <User className="h-10 w-10 text-atl-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-port-gray-900">{currentUser?.nom}</h3>
              <p className="text-sm text-port-gray-600">{formData.poste}</p>
              
              <div className="mt-4">
                <span className={`inline-flex px-3 py-1 text-sm font-medium rounded-full ${roleInfo.color}`}>
                  {roleInfo.label}
                </span>
              </div>
              
              <p className="text-xs text-port-gray-500 mt-2">{roleInfo.description}</p>
            </div>
          </div>

          {/* Informations du compte */}
          <div className="card p-6">
            <h4 className="font-medium text-port-gray-900 mb-4">Informations du compte</h4>
            
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-port-gray-600">ID Utilisateur</span>
                <span className="text-sm font-medium text-port-gray-900">#{currentUser?.id}</span>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm text-port-gray-600">Membre depuis</span>
                <span className="text-sm font-medium text-port-gray-900">Jan 2023</span>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm text-port-gray-600">Dernière connexion</span>
                <span className="text-sm font-medium text-port-gray-900">Aujourd'hui 14:30</span>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm text-port-gray-600">Statut</span>
                <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-atl-green-100 text-atl-green-800">
                  Actif
                </span>
              </div>
            </div>
          </div>

          {/* Permissions */}
          <div className="card p-6">
            <h4 className="font-medium text-port-gray-900 mb-4 flex items-center">
              <Shield className="h-4 w-4 mr-2" />
              Permissions
            </h4>
            
            <div className="space-y-2">
              {currentUser?.permissions?.map((permission, index) => (
                <div key={index} className="flex items-center">
                  <div className="h-2 w-2 bg-atl-green-500 rounded-full mr-2"></div>
                  <span className="text-sm text-port-gray-700">{permission}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profil; 